import React, { useEffect, useRef, useState } from "react";
import { patch } from "@rails/request.js";

// Inline-editable name pill for studio top bars.
//
// Click the name → input + spinner. Enter saves, Escape cancels,
// blur saves. PATCHes the given endpoint with `{ [resourceKey]: { name } }`.
// Calls onChange(newName) on success so the parent can update its
// local state immediately.
const EditableName = ({
  endpoint,
  resourceKey,
  name,
  fallback = "untitled",
  onChange,
  onError,
  onSuccess,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(name || "");
  }, [name]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const cancel = () => {
    setDraft(name || "");
    setEditing(false);
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      onError && onError("Name can't be blank");
      return;
    }
    if (trimmed === name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const response = await patch(endpoint, {
      body: { [resourceKey]: { name: trimmed } },
      contentType: "application/json",
      responseKind: "json",
    });
    setSaving(false);
    if (response.ok) {
      const json = await response.json;
      const updated = json[resourceKey]?.name ?? trimmed;
      onChange && onChange(updated);
      onSuccess && onSuccess();
      setEditing(false);
    } else {
      onError && onError("Couldn't save name");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return (
      <div className="flex-1 min-w-0 flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={onKeyDown}
          disabled={saving}
          className="flex-1 min-w-0 zz-display text-base sm:text-lg font-black leading-tight border-[2px] border-black bg-white px-2 py-1 focus:outline-none focus:bg-ssyellow/20"
        />
        {saving && (
          <svg className="animate-spin h-4 w-4 text-slate-500 flex-none" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Click to rename"
      className="min-w-0 max-w-full inline-flex items-center gap-1.5 group text-left"
    >
      <span className="min-w-0 zz-display text-base sm:text-lg font-black leading-tight truncate group-hover:text-ssblue transition-colors">
        {name || fallback}
      </span>
      <svg
        className="flex-none h-3.5 w-3.5 text-slate-400 group-hover:text-ssblue transition-colors"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="2"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
      </svg>
    </button>
  );
};

export default EditableName;
